import { useState, useRef, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { cx } from '@parrot/utils';
import styles from './AddFilterChip.module.css';

export interface SecondaryFilter {
  id: string;
  label: string;
}

export interface AddFilterChipProps {
  /** Secondary filters that can be surfaced. */
  filters: SecondaryFilter[];
  /** IDs already surfaced — hidden from the menu. */
  activeIds: string[];
  onAdd: (filterId: string) => void;
}

/**
 * Dashed "+ More Filters" pill (adopted from licensing-intelligence). Opens a
 * menu of secondary filters not yet active; picking one surfaces it inline.
 * Renders nothing once every secondary is active.
 */
export function AddFilterChip({ filters, activeIds, onAdd }: AddFilterChipProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const available = filters.filter((f) => !activeIds.includes(f.id));

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (available.length === 0) return null;

  return (
    <div className={styles.wrap} ref={ref}>
      <button
        type={'button'}
        className={cx(styles.trigger, open && styles.triggerOpen)}
        onClick={() => setOpen((o) => !o)}
      >
        <Plus size={14} strokeWidth={2} />
        <span>{'More Filters'}</span>
      </button>
      {open && (
        <div className={styles.menu}>
          {available.map((f) => (
            <button
              key={f.id}
              type={'button'}
              className={styles.option}
              onClick={() => {
                onAdd(f.id);
                setOpen(false);
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
